import React, { useState, useMemo, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Cell } from 'recharts';
import { AlertTriangle, CheckCircle, Download, TrendingUp, FileText, Loader2 } from 'lucide-react';
import { assessments } from '../services/api';

const PortageDashboard = ({ assessmentData }) => {
  const dashboardData = assessmentData?.parsedData || null;
  const loading = false;
  const error = !dashboardData ? 'Nenhuma avaliação processada ainda' : null;
  const dashboardRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  const [parentReport, setParentReport] = useState(assessmentData?.latestAssessment?.parentReport || null);
  const [reportGeneratedAt, setReportGeneratedAt] = useState(assessmentData?.latestAssessment?.parentReportGeneratedAt || null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportError, setReportError] = useState(null);

  // Process the parsed PORTAGE data
  const responseData = useMemo(() => {
    if (!dashboardData?.PORTAGE?.responses) return null;

    const responses = dashboardData.PORTAGE.responses;

    // Calculate overall totals
    const overall = { "SIM": 0, "NÃO": 0, "ÀS VEZES": 0 };

    // Group by area
    const byArea = {};

    // Group by age group
    const byAgeGroup = {};

    responses.forEach(response => {
      // Overall count
      overall[response.response]++;

      // By area
      if (!byArea[response.area]) {
        byArea[response.area] = { SIM: 0, NÃO: 0, "ÀS VEZES": 0, total: 0 };
      }
      byArea[response.area][response.response]++;
      byArea[response.area].total++;

      // By age group
      if (!byAgeGroup[response.ageGroup]) {
        byAgeGroup[response.ageGroup] = { SIM: 0, NÃO: 0, "ÀS VEZES": 0, total: 0 };
      }
      byAgeGroup[response.ageGroup][response.response]++;
      byAgeGroup[response.ageGroup].total++;
    });

    return {
      overall,
      byArea,
      byAgeGroup
    };
  }, [dashboardData]);

  // Calculate percentages for areas
  const areaPercentages = useMemo(() => {
    if (!responseData) return [];
    return Object.entries(responseData.byArea).map(([area, data]) => ({
      area: area.replace('DESENVOLVIMENTO MOTOR', 'MOTOR').replace('SOCIALIZAÇÃO', 'SOCIAL').replace('COGNIÇÃO', 'COGNITIVO'),
      fullArea: area,
      simPercent: Math.round((data.SIM / data.total) * 100),
      noPercent: Math.round((data.NÃO / data.total) * 100),
      sometimesPercent: Math.round((data["ÀS VEZES"] / data.total) * 100),
      total: data.total,
      sim: data.SIM,
      no: data.NÃO,
      sometimes: data["ÀS VEZES"]
    }));
  }, [responseData]);

  // Calculate age group progression
  const ageProgressData = useMemo(() => {
    if (!responseData) return [];
    return Object.entries(responseData.byAgeGroup).map(([age, data]) => ({
      age,
      percentage: Math.round((data.SIM / data.total) * 100),
      mastered: data.SIM,
      total: data.total,
      developing: data["ÀS VEZES"],
      notMastered: data.NÃO
    }));
  }, [responseData]);

  // Color helper — Portage uses lower thresholds (60/40) since it covers broader developmental scope
  function getBarColor(percentage) {
    if (percentage >= 60) return '#10B981';  // Green
    if (percentage >= 40) return '#F59E0B';  // Yellow
    return '#EF4444';                        // Red
  }

  // Custom tooltip for area chart
  const AreaTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
          <p className="font-medium text-gray-800">{data.fullArea || data.area}</p>
          <p className="text-green-600">SIM (Dominado): {data.sim}/{data.total} ({data.simPercent}%)</p>
          <p className="text-yellow-600">ÀS VEZES (Emergindo): {data.sometimes} ({data.sometimesPercent}%)</p>
          <p className="text-red-600">NÃO: {data.no} ({data.noPercent}%)</p>
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for age progression chart
  const AgeTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
          <p className="font-medium text-gray-800">Faixa Etária: {data.age} anos</p>
          <p className="text-green-600">Dominados: {data.mastered}/{data.total}</p>
          <p className="text-yellow-600">Emergindo: {data.developing}</p>
          <p className="text-red-600">Não dominados: {data.notMastered}</p>
          <p className="text-brand-600 font-medium">Taxa: {data.percentage}%</p>
        </div>
      );
    }
    return null;
  };

  // Generate parent report
  const generateParentReport = async () => {
    const assessmentId = assessmentData?.latestAssessment?.id;
    if (!assessmentId) return;

    setIsGeneratingReport(true);
    setReportError(null);
    try {
      const response = await assessments.generateParentReport(assessmentId);
      if (response.data.status === 'success') {
        setParentReport(response.data.data.report);
        setReportGeneratedAt(new Date().toISOString());
      } else {
        setReportError(response.data.message || 'Erro ao gerar relatório');
      }
    } catch (err) {
      console.error('Error generating parent report:', err);
      setReportError(err.response?.data?.message || 'Erro ao gerar relatório. Tente novamente.');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  // Simple markdown renderer
  const renderMarkdown = (markdown) => {
    if (!markdown) return null;
    const lines = markdown.split('\n');
    const elements = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      if (line.startsWith('### ')) {
        elements.push(
          <h3 key={i} className="text-lg font-semibold text-gray-800 mt-6 mb-2">
            {line.replace('### ', '')}
          </h3>
        );
      } else if (line.startsWith('## ')) {
        elements.push(
          <h2 key={i} className="text-xl font-bold text-gray-900 mt-6 mb-3">
            {line.replace('## ', '')}
          </h2>
        );
      } else if (line.startsWith('- ') || line.startsWith('* ')) {
        const items = [];
        while (i < lines.length && (lines[i].startsWith('- ') || lines[i].startsWith('* '))) {
          items.push(
            <li key={i} className="text-gray-700 mb-1">
              {renderInlineMarkdown(lines[i].replace(/^[-*]\s/, ''))}
            </li>
          );
          i++;
          while (i < lines.length && lines[i].trim() === '') i++;
        }
        elements.push(
          <ul key={`ul-${i}`} className="ml-4 list-disc mb-2">
            {items}
          </ul>
        );
        continue;
      } else if (/^\d+\.\s/.test(line)) {
        // Collect consecutive numbered items (with description paragraphs) into an <ol>
        const items = [];
        while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
          const titleLine = lines[i].replace(/^\d+\.\s/, '');
          i++;
          while (i < lines.length && lines[i].trim() === '') i++;
          const descParts = [];
          while (i < lines.length && !/^\d+\.\s/.test(lines[i]) && !lines[i].startsWith('#') && !lines[i].startsWith('---')) {
            if (lines[i].trim() === '') { i++; continue; }
            descParts.push(
              <p key={`desc-${i}`} className="text-gray-700 mt-1 leading-relaxed">
                {renderInlineMarkdown(lines[i])}
              </p>
            );
            i++;
          }
          items.push(
            <li key={`li-${items.length}`} className="text-gray-700 mb-3">
              {renderInlineMarkdown(titleLine)}
              {descParts}
            </li>
          );
        }
        elements.push(
          <ol key={`ol-${i}`} className="ml-4 list-decimal mb-2">
            {items}
          </ol>
        );
        continue;
      } else if (line.startsWith('---')) {
        elements.push(<hr key={i} className="my-4 border-gray-200" />);
      } else if (line.trim() === '') {
        elements.push(<div key={i} className="h-2" />);
      } else {
        elements.push(
          <p key={i} className="text-gray-700 mb-2 leading-relaxed">
            {renderInlineMarkdown(line)}
          </p>
        );
      }
      i++;
    }
    return elements;
  };

  const renderInlineMarkdown = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={idx}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  // PDF Export Function
  const exportToPDF = async () => {
    if (!dashboardRef.current) return;

    setIsExporting(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;

      const patient = assessmentData?.latestAssessment?.patient;
      const patientName = patient ? `${patient.firstName} ${patient.lastName}` : 'Paciente';
      const date = new Date().toISOString().split('T')[0];
      const filename = `Portage_Dashboard_${patientName.replace(/\s+/g, '_')}_${date}.pdf`;

      // Fix width so PDF looks the same regardless of screen/window size
      const el = dashboardRef.current;
      const originalWidth = el.style.width;
      el.style.width = '1024px';

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        height: el.scrollHeight,
        width: 1024
      });

      el.style.width = originalWidth;

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const margin = 10;
      const ratio = (pdfWidth - 2 * margin) / imgWidth;
      const scaledWidth = pdfWidth - 2 * margin;
      const scaledHeight = imgHeight * ratio;

      const x = margin;
      const y = 10;

      if (scaledHeight > pdfHeight - 20) {
        let position = 0;
        const pageHeight = pdfHeight - 20;

        while (position < scaledHeight) {
          if (position > 0) {
            pdf.addPage();
          }

          const remainingHeight = scaledHeight - position;
          const currentPageHeight = Math.min(pageHeight, remainingHeight);

          const tempCanvas = document.createElement('canvas');
          const tempCtx = tempCanvas.getContext('2d');

          tempCanvas.width = imgWidth;
          tempCanvas.height = (currentPageHeight / ratio);

          tempCtx.drawImage(canvas,
            0, position / ratio, imgWidth, currentPageHeight / ratio,
            0, 0, imgWidth, currentPageHeight / ratio
          );

          const tempImgData = tempCanvas.toDataURL('image/png');
          pdf.addImage(tempImgData, 'PNG', x, y, scaledWidth, currentPageHeight);

          position += currentPageHeight;
        }
      } else {
        pdf.addImage(imgData, 'PNG', x, y, scaledWidth, scaledHeight);
      }

      // Add version footer to all pages
      const { getVersionString } = await import('../config/version');
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(getVersionString(), pdfWidth / 2, pdfHeight - 5, { align: 'center' });
      }

      pdf.save(filename);

    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      alert('Erro ao exportar PDF. Tente novamente.');
    } finally {
      setIsExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-lg text-gray-600">Carregando dashboard...</div>
      </div>
    );
  }

  if (error || !dashboardData?.PORTAGE || !responseData) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <AlertTriangle className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
        <div className="text-yellow-800 font-medium mb-2">Dashboard de Avaliação Portage</div>
        <div className="text-yellow-700 text-sm">
          {error || 'A avaliação precisa ser analisada antes que o dashboard possa ser exibido.'}
        </div>
      </div>
    );
  }

  const totalItems = dashboardData.PORTAGE.metadata.totalItems;
  const totalSim = responseData.overall["SIM"];
  const totalSometimes = responseData.overall["ÀS VEZES"];
  const overallPercent = Math.round((totalSim / totalItems) * 100);

  const patientInfo = {
    name: assessmentData?.latestAssessment?.patient
      ? `${assessmentData.latestAssessment.patient.firstName} ${assessmentData.latestAssessment.patient.lastName}`
      : 'Paciente',
    assessmentDate: new Date().toLocaleDateString('pt-BR')
  };

  // Sort areas by percentage for priority display
  const sortedAreas = [...areaPercentages].sort((a, b) => a.simPercent - b.simPercent);
  const priorityAreas = sortedAreas.filter(a => a.simPercent < 40);
  const strongAreas = [...areaPercentages].sort((a, b) => b.simPercent - a.simPercent);

  return (
    <div className="space-y-6">
      {/* Export Button */}
      <div className="flex justify-end">
        <button
          onClick={exportToPDF}
          disabled={isExporting}
          className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
            isExporting
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-brand-700 hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500'
          }`}
        >
          {isExporting ? (
            <>
              <div className="animate-spin -ml-1 mr-3 h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
              Exportando...
            </>
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              Exportar PDF
            </>
          )}
        </button>
      </div>

      {/* Dashboard Content */}
      <div ref={dashboardRef} className="bg-white">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Dashboard de Avaliação Portage</h2>
              <p className="text-sm text-gray-600 mt-1">
                {patientInfo.name} • Avaliação: {patientInfo.assessmentDate} • {totalItems} itens avaliados
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Summary Metric Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-brand-500">
              <div className="flex items-center">
                <TrendingUp className="h-8 w-8 text-brand-600 mr-3" />
                <div>
                  <div className="text-sm text-gray-600">Itens Totais</div>
                  <div className="text-2xl font-bold text-gray-900">{totalItems}</div>
                  <div className="text-xs text-gray-500">5 áreas • 6 faixas etárias</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-green-500">
              <div className="flex items-center">
                <CheckCircle className="h-8 w-8 text-green-600 mr-3" />
                <div>
                  <div className="text-sm text-gray-600">Dominados (SIM)</div>
                  <div className="text-2xl font-bold text-gray-900">{totalSim}</div>
                  <div className="text-xs text-gray-500">{overallPercent}% do total</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-yellow-500">
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-full bg-yellow-500 text-white flex items-center justify-center mr-3">
                  <span className="text-sm font-bold">E</span>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Emergindo (ÀS VEZES)</div>
                  <div className="text-2xl font-bold text-gray-900">{totalSometimes}</div>
                  <div className="text-xs text-gray-500">Em desenvolvimento</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-purple-500">
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-full bg-purple-500 text-white flex items-center justify-center mr-3">
                  <span className="text-sm font-bold">%</span>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Taxa de Progresso</div>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {areaPercentages.slice(0, 5).map((a, i) => (
                      <div key={i}>
                        <div className="text-sm font-bold" style={{ color: getBarColor(a.simPercent) }}>{a.simPercent}%</div>
                        <div className="text-xs text-gray-400">{a.area.substring(0, 4)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* How to read this report */}
          <div className="bg-brand-50 border border-brand-200 rounded-lg p-4 text-sm text-gray-700">
            <p className="font-medium text-gray-800 mb-2">Como ler este relatório</p>
            <p className="mb-1">
              O Inventário Portage avalia <strong>5 áreas</strong> do desenvolvimento: <strong>Socialização</strong> (interação social), <strong>Linguagem</strong> (compreensão e expressão), <strong>Cognição</strong> (raciocínio e conceitos), <strong>Autocuidados</strong> (independência) e <strong>Desenvolvimento Motor</strong> (motricidade grossa e fina).
            </p>
            <p className="mb-1">
              As habilidades são organizadas em <strong>6 faixas etárias</strong>: 0-1, 1-2, 2-3, 3-4, 4-5 e 5-6 anos. Cada item é avaliado como <strong>SIM</strong> (dominado), <strong>ÀS VEZES</strong> (emergindo) ou <strong>NÃO</strong> (não dominado).
            </p>
            <p>
              Nos gráficos: <span className="inline-block w-3 h-3 rounded-sm align-middle" style={{backgroundColor:'#10B981'}}></span> <strong>Verde</strong> (≥60%) = bom domínio &nbsp;•&nbsp; <span className="inline-block w-3 h-3 rounded-sm align-middle" style={{backgroundColor:'#F59E0B'}}></span> <strong>Amarelo</strong> (40–59%) = domínio moderado &nbsp;•&nbsp; <span className="inline-block w-3 h-3 rounded-sm align-middle" style={{backgroundColor:'#EF4444'}}></span> <strong>Vermelho</strong> (&lt;40%) = precisa de atenção
            </p>
          </div>

          {/* Performance by Developmental Area — Color-coded bars */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-1">Performance por Área Desenvolvimental</h3>
            <p className="text-sm text-gray-500 mb-4">Taxa de domínio (SIM) por área — barras coloridas por nível de desempenho</p>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={areaPercentages} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="area"
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    fontSize={12}
                    interval={0}
                  />
                  <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
                  <Tooltip content={<AreaTooltip />} />
                  <Bar dataKey="simPercent" name="Dominado (%)" radius={[4, 4, 0, 0]}>
                    {areaPercentages.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={getBarColor(entry.simPercent)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Developmental Progression by Age — with colored dots */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-1">Progressão de Desenvolvimento por Faixa Etária</h3>
            <p className="text-sm text-gray-500 mb-4">Taxa de domínio (SIM) em cada faixa etária</p>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={ageProgressData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="age" />
                  <YAxis domain={[0, 100]} label={{ value: 'Taxa de Sucesso (%)', angle: -90, position: 'insideLeft' }} />
                  <Tooltip content={<AgeTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="percentage"
                    stroke="#2563eb"
                    strokeWidth={3}
                    dot={({ cx, cy, payload }) => (
                      <circle
                        key={`dot-${payload.age}`}
                        cx={cx}
                        cy={cy}
                        r={7}
                        fill={getBarColor(payload.percentage)}
                        stroke="#fff"
                        strokeWidth={2}
                      />
                    )}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Combined Analysis Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Detailed Area Breakdown */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Análise Detalhada por Área</h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Área</th>
                      <th className="text-center p-2">Dominado</th>
                      <th className="text-center p-2">Taxa de Sucesso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {areaPercentages.map((area, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="p-2 font-medium">{area.area}</td>
                        <td className="p-2 text-center">
                          <span className="text-sm">{area.sim}/{area.total}</span>
                        </td>
                        <td className="p-2 text-center">
                          <div className="flex items-center justify-center">
                            <div className="w-16 h-4 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full transition-all duration-300"
                                style={{
                                  width: `${area.simPercent}%`,
                                  backgroundColor: getBarColor(area.simPercent)
                                }}
                              ></div>
                            </div>
                            <span className="ml-2 text-sm font-medium">{area.simPercent}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Age Group Performance */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Performance por Faixa Etária</h3>
              <div className="space-y-3">
                {ageProgressData.map((age, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <span className="font-medium">{age.age} anos</span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm">{age.mastered}/{age.total}</span>
                      <div className="w-20 h-3 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full transition-all duration-300"
                          style={{
                            width: `${age.percentage}%`,
                            backgroundColor: getBarColor(age.percentage)
                          }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium w-12">{age.percentage}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Clinical Recommendations — Two columns */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Recomendações Clínicas</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-800 mb-3">Áreas Prioritárias</h4>
                <div className="space-y-2">
                  {priorityAreas.length > 0 ? (
                    priorityAreas.slice(0, 3).map((area, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-red-50 rounded">
                        <span className="text-sm font-medium">{area.fullArea || area.area}</span>
                        <span className="text-sm text-red-600">{area.simPercent}%</span>
                      </div>
                    ))
                  ) : (
                    sortedAreas.slice(0, 3).map((area, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-yellow-50 rounded">
                        <span className="text-sm font-medium">{area.fullArea || area.area}</span>
                        <span className="text-sm text-yellow-600">{area.simPercent}%</span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-800 mb-3">Próximos Passos</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                    <span>Intervenção focada nas áreas prioritárias ({sortedAreas.slice(0, 2).map(a => a.area).join(', ')})</span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-yellow-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                    <span>Estimular habilidades emergentes ({totalSometimes} itens "ÀS VEZES") com atividades práticas</span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                    <span>Reavaliação em 6 meses para medir progresso</span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-brand-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
                    <span>Meta: {sortedAreas.slice(0, 2).map(a => `${a.area} ≥ ${Math.max(50, a.simPercent + 15)}%`).join(' | ')} em 12 meses</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Parent Report Section */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Relatório para Família</h3>
                <p className="text-sm text-gray-500">
                  Relatório em linguagem acessível para compartilhar com os pais
                  {reportGeneratedAt && (
                    <span className="ml-2 text-gray-400">
                      — Gerado em {new Date(reportGeneratedAt).toLocaleDateString('pt-BR')} às {new Date(reportGeneratedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </p>
              </div>
              <button
                onClick={generateParentReport}
                disabled={isGeneratingReport}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                  isGeneratingReport
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-brand-700 hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500'
                }`}
              >
                {isGeneratingReport ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                    Gerando...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    {parentReport ? 'Gerar Novamente' : 'Gerar Relatório'}
                  </>
                )}
              </button>
            </div>

            {reportError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-700">{reportError}</p>
              </div>
            )}

            {parentReport && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 prose prose-sm max-w-none">
                {renderMarkdown(parentReport)}
              </div>
            )}

            {!parentReport && !reportError && !isGeneratingReport && (
              <div className="text-center py-8 text-gray-400">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">Clique em "Gerar Relatório" para criar um relatório em linguagem acessível para os pais.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortageDashboard;
